/**
 * Checkerboard - a class to generate a checkerboard pattern for UV mapping
 * @param {number} w - width of the pattern
 * @param {number} h - height of the pattern
 */

export class UVGridGenerator {
  constructor(w, h) {
    this.cols = 8;
    this.rows = 8;
    this.miniCellCount = 8;
    this.width = w;
    this.height = h;
    this.cellSize = this.width / this.cols;
    this.cellMarginScale = 0.08;
    this.cellMargin = this.cellSize * this.cellMarginScale;
    this.colors = [
      "#FF595E",
      "#FF924C",
      "#FFCA3A",
      "#8AC926",
      "#52A675",
      "#1982C4",
      "#4267AC",
      "#6A4C93",
    ];
  }

  generatePattern() {
    let fb = createFramebuffer({ width: this.width, height: this.height, depth: false, stencil: false });

    fb.begin();
    background(220);
    noStroke();

    // translate for WEBGL mode (0,0 is at center)
    translate(-this.width / 2, -this.height / 2);

    for (let x = 0; x < this.cols; x++) {
      for (let y = 0; y < this.rows; y++) {
        let px = x * this.cellSize;
        let py = y * this.cellSize;

        if ((x + y) % 2 == 0) {
          this.drawLetterCell(px, py, x, y);
        } else {
          this.drawCheckerCell(px, py, x, y);
        }

        noFill();
        strokeWeight(this.cellSize * 0.04);
        stroke(50);
        rect(px, py, this.cellSize, this.cellSize);
      }
    }

    fb.end();

    return fb;
  }

  drawLetterCell(x, y, col, row) {
    noStroke();

    let diagonalIndex = Math.floor((row + col) / 2) % this.colors.length;
    let gradientColor = color(this.colors[diagonalIndex]);

    fill(gradientColor);
    rect(x, y, this.cellSize, this.cellSize);

    let dotSize = this.cellSize * 0.075;
    let recSize = this.cellSize * 0.07;
    let m = this.cellMargin;

    // corner circles
    push();
    noStroke();
    fill(255, 200);
    ellipse(x + m, y + m, dotSize); // top left
    ellipse(x + this.cellSize - m, y + this.cellSize - m, dotSize); // bottom right
    pop();

    // corner squares
    push();
    blendMode(MULTIPLY);
    fill(0, 150);
    rectMode(CENTER);
    rect(x + m, y + this.cellSize - m, recSize, recSize); // bottom left
    rect(x + this.cellSize - m, y + m, recSize, recSize); // top right
    pop();

    // bottom half rectangle (covers the bottom half of the cell)
    push();
    fill(255, 50);
    rect(x, y + this.cellSize / 2, this.cellSize, this.cellSize / 2);
    pop();

    // center letter
    textSize(this.cellSize * 0.7);
    textAlign(CENTER, CENTER);
    fill(255, 255, 255, 200);
    text(
      char(65 + (row % 26)),
      x + this.cellSize * 0.5,
      y + this.cellSize * 0.55
    );
  }

  drawCheckerCell(x, y, col, row) {
    noStroke();

    for (
      let i = 0;
      i < this.cellSize;
      i += this.cellSize / this.miniCellCount
    ) {
      for (
        let j = 0;
        j < this.cellSize;
        j += this.cellSize / this.miniCellCount
      ) {
        if ((i + j) % ((2 * this.cellSize) / this.miniCellCount) == 0) {
          fill(180);
        } else {
          fill(200);
        }
        rect(x + i, y + j, this.cellSize / 5, this.cellSize / 5);
      }
    }

    let offset = this.cellMargin;
    stroke(255, 255, 255, 220);
    strokeCap(SQUARE);
    strokeWeight(this.cellSize * 0.055);
    line(
      x + offset,
      y + this.cellSize / 2,
      x + this.cellSize - offset,
      y + this.cellSize / 2
    );
    line(
      x + this.cellSize / 2,
      y + offset,
      x + this.cellSize / 2,
      y + this.cellSize - offset
    );

    noStroke();
    fill(0);
    textSize(this.cellSize * 0.15);
    textAlign(LEFT, TOP);
    text(nf(row, 2), x + offset / 2, y + offset / 2);
    textAlign(RIGHT, BOTTOM);
    text(
      nf(col, 2),
      x + this.cellSize - offset / 2,
      y + this.cellSize - offset / 2
    );
  }
}
